/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { LocalStorageRefService } from "./local-storage-ref.service";
import { LocalStorageOptions } from "./local-storage.module";
import { LOCAL_STORAGE_OPTIONS } from "./tokens";

export interface LocalStorageData {
  age: number | undefined;
  data: Record<string, any>;
}

@Injectable({
  providedIn: "root"
})
export class LocalStorageService {
  private options = inject<LocalStorageOptions>(LOCAL_STORAGE_OPTIONS, { optional: true });

  private _localStorage: Storage;
  private _localStorage$ = new BehaviorSubject<LocalStorageData | null>(null);

  public localStorage$ = this._localStorage$.asObservable();

  constructor() {
    const ref = inject(LocalStorageRefService);

    this._localStorage = ref.localStorage;
  }

  // Unix date in seconds
  private now = () => Math.floor(Date.now() / 1000);

  set(data: Partial<LocalStorageData>) {
    const _data = {
      age: this.now(),
      ...data
    } as LocalStorageData;
    if (this.options) {
      this._localStorage.setItem(this.options.storageName, JSON.stringify(_data));
    }
    this._localStorage$.next(_data);
  }

  load() {
    const data = this.options ? this._localStorage.getItem(this.options.storageName) : null;
    if (data !== null) {
      const current: LocalStorageData = JSON.parse(data) as LocalStorageData;
      if (this.options && current.age && this.now() - current.age < this.options.maxAgeSeconds) {
        this._localStorage$.next(current);
      } else {
        if (this.options) {
          this.clear(this.options.storageName);
        }
      }
    }
  }

  clear(name: string) {
    this._localStorage.removeItem(name);
    this._localStorage$.next(null);
  }

  /**
   * Removes this service's own entry. Other features keep their keys in the same
   * origin storage (the notification tray, vCI view preferences), so a post-login
   * redirect mustn't wipe them.
   */
  clearAll() {
    if (this.options) {
      this.clear(this.options.storageName);
    } else {
      this._localStorage$.next(null);
    }
  }
}
